import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { Views } from 'react-big-calendar';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa6';
import { CALENDAR_VIEW_OPTIONS } from '../../../constants/settings';
import FilterButton from '../Buttons/FilterButton';

const AcademiaCalendarToolbar = ({
  currentView,
  date,
  setCurrentView,
  setDate,
  layoutId,
}: {
  currentView: any;
  date: Date;
  setCurrentView: (value: any) => void;
  setDate: React.Dispatch<React.SetStateAction<Date>>;
  layoutId: string;
}) => {
  // converting date to desired formatted text here
  const dateText = useMemo(() => {
    switch (currentView) {
      case Views.DAY:
        return moment(date).format('dddd, MMMM, DD');

      case Views.WEEK:
        const from = moment(date).startOf('week');
        const to = moment(date).endOf('week');

        return `${from.format('MMMM DD')} to ${to.format('MMMM DD')}`;

      case Views.MONTH:
        return moment(date).format('MMMM YYYY');

      default:
        return '';
    }
  }, [currentView, date]);

  // handler function to handle previous button click declared here
  const prevBtnHandler = useCallback(() => {
    switch (currentView) {
      case Views.DAY:
        setDate(moment(date).subtract(1, 'd').toDate());
        break;

      case Views.WEEK:
        setDate(moment(date).subtract(1, 'w').toDate());
        break;

      case Views.MONTH:
        setDate(moment(date).subtract(1, 'M').toDate());
        break;

      default:
        break;
    }
  }, [currentView, date, setDate]);

  // handler function to handle next button click declared here
  const nextBtnHandler = useCallback(() => {
    switch (currentView) {
      case Views.DAY:
        setDate(moment(date).add(1, 'd').toDate());
        break;

      case Views.WEEK:
        setDate(moment(date).add(1, 'w').toDate());
        break;

      case Views.MONTH:
        setDate(moment(date).add(1, 'M').toDate());
        break;

      default:
        break;
    }
  }, [currentView, date, setDate]);

  // rendering custom calendar toolbar component here
  return (
    <div className='flex flex-col xl:flex-row items-center justify-between gap-4 mb-4'>
      <div className='flex flex-col md:flex-row items-center gap-4 text-legal-dark-blue'>
        <button
          onClick={() => setDate(moment().toDate())}
          className='px-2 py-3 rounded-lg shadow-academia-sm w-full md:w-20 hover:opacity-70 active:scale-95 duration-300'
        >
          Today
        </button>

        <div className='flex items-stretch rounded-lg shadow-academia-sm overflow-hidden'>
          <button
            onClick={prevBtnHandler}
            className='block px-3 hover:opacity-70 hover:bg-base-light-gray active:scale-95 duration-300'
          >
            <FaAngleLeft className='size-5' />
          </button>

          <div className='flex items-center justify-center py-3 bg-legal-dark-blue text-base-white font-semibold text-sm md:text-base w-[250px] md:w-[300px]'>
            <p>{dateText}</p>
          </div>

          <button
            onClick={nextBtnHandler}
            className='px-3 hover:opacity-70 hover:bg-base-light-gray active:scale-95 duration-300'
          >
            <FaAngleRight className='size-5' />
          </button>
        </div>
      </div>

      <div className='flex items-center gap-2 px-2 py-3 rounded-lg shadow-academia-sm'>
        {CALENDAR_VIEW_OPTIONS.map((option) => (
          <FilterButton
            key={option.id}
            keyId={option.id}
            onClick={setCurrentView}
            selectedValue={currentView}
            btnText={option.label}
            layoutId={layoutId}
            color={option.color}
            width={100}
          />
        ))}
      </div>
    </div>
  );
};

export default AcademiaCalendarToolbar;
